import { useState, useEffect, useCallback, useRef } from 'react'

const INACTIVITY_LIMIT_MS = 15 * 60 * 1000
const WARNING_MS = 2 * 60 * 1000

export function useSessionTimeout({ onTimeout, onWarning, onActivity }) {
  const [showWarning, setShowWarning] = useState(false)
  const [timeLeft, setTimeLeft] = useState(WARNING_MS)
  const lastActivity = useRef(Date.now())
  const intervalRef = useRef(null)

  const reset = useCallback(() => {
    lastActivity.current = Date.now()
    setShowWarning(false)
    setTimeLeft(WARNING_MS)
    onActivity?.()
  }, [onActivity])

  useEffect(() => {
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll']

    const handleActivity = () => {
      if (showWarning) return
      lastActivity.current = Date.now()
    }

    events.forEach((e) => window.addEventListener(e, handleActivity, { passive: true }))

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivity.current
      if (elapsed >= INACTIVITY_LIMIT_MS) {
        setShowWarning(false)
        clearInterval(intervalRef.current)
        onTimeout?.()
      } else if (elapsed >= INACTIVITY_LIMIT_MS - WARNING_MS) {
        setShowWarning(true)
        setTimeLeft(INACTIVITY_LIMIT_MS - elapsed)
        onWarning?.()
      } else if (showWarning) {
        setShowWarning(false)
      }
    }, 1000)

    return () => {
      events.forEach((e) => window.removeEventListener(e, handleActivity))
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [onTimeout, onWarning, onActivity, showWarning])

  return { showWarning, timeLeft, reset, setShowWarning }
}
